import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Loader2, Save, ShieldAlert, Clock, Mail, Info, CheckCircle2 } from 'lucide-react';
import { configApi, RestrictionConfig } from '../../api/config.api';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

export function RestrictionsConfig() {
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<Partial<RestrictionConfig>>({});

    const loadConfig = useCallback(async () => {
        try {
            setLoading(true);
            const data = await configApi.getRestrictions();
            setFormData(data);
        } catch (err) {
            console.error('Error loading restrictions:', err);
            toast({ title: 'Erè', description: 'Pa kapab chaje konfigirasyon restriksyon yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const handleSave = async () => {
        try {
            setSaving(true);
            await configApi.updateRestrictions(formData);
            toast({ title: 'Siksè', description: 'Konfigirasyon restriksyon yo aktyalize', type: 'success' });
        } catch (err) {
            console.error('Error saving restrictions:', err);
            toast({ title: 'Erè', description: 'Pa kapab sovgade chanjman yo', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <span className="ml-3 text-slate-600">Chajman konfigirasyon...</span>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">Restriksyon Tranzaksyon</h2>
                    <p className="text-slate-500 mt-1">Jere limit montant ak restriksyon orè pou ajan yo.</p>
                </div>
                <div className="flex items-center gap-3 bg-slate-100 p-1 rounded-lg">
                    <span className="text-xs font-semibold px-2 text-slate-600">Estati Sistèm:</span>
                    <div className="flex items-center gap-2 pr-2">
                        <Switch 
                            checked={formData.is_active} 
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        />
                        <span className={`text-xs font-bold ${formData.is_active ? 'text-green-600' : 'text-red-500'}`}>
                            {formData.is_active ? 'AKTIF' : 'INAKTIF'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Amount Limits */}
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                    <CardHeader className="bg-slate-50 border-b pb-4">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-amber-500" />
                            <CardTitle className="text-lg">Limit Montant Elevé</CardTitle>
                        </div>
                        <CardDescription>Tranzaksyon ki depase limit sa yo ap rete an atant validasyon Admin.</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Dépôt/Retrait BS (HTG)</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-slate-400 font-medium">HTG</span>
                                <Input 
                                    type="number"
                                    className="pl-12"
                                    value={formData.limit_bs_amount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, limit_bs_amount: Number(e.target.value) }))}
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                <Info className="h-3 w-3" /> Default: 45,000 HTG
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Dépôt SOL (HTG)</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-slate-400 font-medium">HTG</span>
                                <Input 
                                    type="number"
                                    className="pl-12"
                                    value={formData.limit_sol_amount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, limit_sol_amount: Number(e.target.value) }))}
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                <Info className="h-3 w-3" /> Default: 100,000 HTG
                            </p>
                        </div>

                        <div className="pt-4 border-t border-slate-100 space-y-2">
                             <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                 <ShieldAlert className="h-4 w-4 text-primary-600" />
                                 Limit Sekirite Admin (HTG)
                             </label>
                             <div className="relative">
                                 <span className="absolute left-3 top-2.5 text-slate-400 font-medium">HTG</span>
                                 <Input 
                                     type="number"
                                     className="pl-12 border-primary-100 focus:ring-primary-500"
                                     value={formData.limit_admin_amount}
                                     onChange={(e) => setFormData(prev => ({ ...prev, limit_admin_amount: Number(e.target.value) }))}
                                 />
                             </div>
                             <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                 <Info className="h-3 w-3" /> Même l'admin sera bloqué si ce seuil est dépassé. (Default: 300,000 HTG)
                             </p>
                         </div>
                    </CardContent>
                </Card>

                {/* Time & Notifications */}
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                    <CardHeader className="bg-slate-50 border-b pb-4">
                        <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-blue-500" />
                            <CardTitle className="text-lg">Orè & Notifikasyon</CardTitle>
                        </div>
                        <CardDescription>Kontwòl sou lè ajan ka fè tranzaksyon ak kote pou voye alèt.</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Lè Limite Ajan (Agent Cutoff)</label>
                            <div className="relative">
                                <Clock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                                <Input 
                                    type="time"
                                    className="pl-10"
                                    value={formData.agent_cutoff_time}
                                    onChange={(e) => setFormData(prev => ({ ...prev, agent_cutoff_time: e.target.value }))}
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                <Info className="h-3 w-3" /> Apre lè sa a, tout tranzaksyon ajan ap refize (Pòtoprens Time).
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Email Administratè</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                                <Input 
                                    type="email"
                                    className="pl-10"
                                    placeholder="admin@bousanm.com"
                                    value={formData.admin_email}
                                    onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                <Info className="h-3 w-3" /> Adrès kote y'ap voye alèt pou tranzaksyon ki bezwen verifikasyon.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="flex items-center justify-between p-4 bg-primary-50 rounded-xl border border-primary-100">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-500 text-white rounded-full">
                        <CheckCircle2 className="h-5 w-5" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-primary-900">Pare pou sove chanjman yo?</p>
                        <p className="text-xs text-primary-700">Chanjman yo ap aplike imedyatman sou tout tranzaksyon ki ap fèt.</p>
                    </div>
                </div>
                <Button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-8 gap-2 shadow-md transition-all active:scale-95"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Anrejistre Modifikasyon
                </Button>
            </div>
        </div>
    );
}
